import { create } from 'zustand';
import {
  getLanguageByCode,
  isRTL,
  type Language,
  SUPPORTED_LANGUAGES,
} from '@/models/language.model';

/**
 * @fileoverview Language Store (Zustand)
 * @description
 * React/Zustand port of the Angular `LanguageService`. Manages application
 * language and i18n with RTL support. Method names, signal names, behaviour,
 * and the localStorage persistence key (`userLanguage`) are reproduced 1:1.
 *
 * Adaptation notes:
 * - The Angular `TranslateService` (ngx-translate) is represented by a minimal
 *   injectable translator contract. `setLanguage` calls `translate.use(lang)`
 *   exactly as Angular did. `initialize()` reads the browser language via
 *   `translate.getBrowserLang()`. A default translator backed by
 *   `navigator.language` is supplied so the store works standalone.
 */

/** Minimal ngx-translate-like contract used by the language store. */
export interface Translator {
  use(lang: string): void;
  getBrowserLang(): string | undefined;
}

/** localStorage key for the saved language preference (matches Angular exactly). */
const STORAGE_KEY = 'userLanguage';

/** Default translator: no-op `use`, browser language from navigator. */
const defaultTranslator: Translator = {
  use: () => {
    /* no-op: only English translations exist */
  },
  getBrowserLang: () => {
    if (typeof navigator === 'undefined' || !navigator.language) {
      return undefined;
    }
    // ngx-translate returns the primary subtag, e.g. 'en' from 'en-US'.
    return navigator.language.split('-')[0];
  },
};

interface LanguageState {
  /** Current selected language code */
  currentLanguage: string;
  /** All supported languages */
  readonly supportedLanguages: Language[];
  /** Currently available languages (only English for now) */
  readonly availableLanguages: string[];
  /** Injected translator (defaults to navigator-backed translator). */
  translate: Translator;

  setLanguage: (langCode: string) => void;
  getLanguage: (code: string) => Language | undefined;
  getCurrentLanguageObj: () => Language;
  isLanguageAvailable: (code: string) => boolean;
  getLanguagesByRegion: (region: Language['region']) => Language[];
  /** Replace the injected translator (test/wiring helper). */
  setTranslator: (translator: Translator) => void;
  /** Run the Angular constructor's initialize() logic. */
  initialize: () => void;
}

/** Save language preference to localStorage (was private saveLanguage). */
function saveLanguage(langCode: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, langCode);
  } catch (error) {
    console.error('Failed to save language preference:', error);
  }
}

/** Get saved language preference from localStorage (was private getSavedLanguage). */
function getSavedLanguage(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch (error) {
    console.error('Failed to load language preference:', error);
    return null;
  }
}

/** Set text direction based on language (was private setTextDirection). */
function setTextDirection(langCode: string): void {
  if (isRTL(langCode)) {
    document.documentElement.dir = 'rtl';
    document.body.classList.add('rtl');
    document.body.classList.remove('ltr');
  } else {
    document.documentElement.dir = 'ltr';
    document.body.classList.add('ltr');
    document.body.classList.remove('rtl');
  }
}

export const useLanguageStore = create<LanguageState>((set, get) => ({
  currentLanguage: 'en',
  supportedLanguages: SUPPORTED_LANGUAGES,
  availableLanguages: ['en'],
  translate: defaultTranslator,

  setLanguage: (langCode) => {
    // For now, only English translations exist; non-available languages still
    // get stored so the UI can reflect the selection ("Coming Soon").
    const { availableLanguages, translate } = get();
    const actualLang = availableLanguages.includes(langCode) ? langCode : 'en';

    translate.use(actualLang);
    set({ currentLanguage: langCode });
    saveLanguage(langCode);

    // Handle RTL languages
    setTextDirection(langCode);

    // Update HTML lang attribute
    document.documentElement.lang = langCode;
  },

  getLanguage: (code) => getLanguageByCode(code),

  getCurrentLanguageObj: () =>
    getLanguageByCode(get().currentLanguage) || SUPPORTED_LANGUAGES[0],

  isLanguageAvailable: (code) => get().availableLanguages.includes(code),

  getLanguagesByRegion: (region) =>
    get().supportedLanguages.filter((lang) => lang.region === region),

  setTranslator: (translator) => set({ translate: translator }),

  initialize: () => {
    const { availableLanguages, translate, setLanguage } = get();
    const savedLang = getSavedLanguage();
    const browserLang = translate.getBrowserLang() || 'en';

    // Use saved language, or browser language if available, otherwise English
    const initialLang =
      savedLang || (availableLanguages.includes(browserLang) ? browserLang : 'en');

    setLanguage(initialLang);
  },
}));
